import { PreferencesApiError, type StoredPreferences } from '@peruselab/api-client/preferences';

export const MAX_FAVOURITE_TEMPLATES = 12;
export const FAVOURITES_STORAGE_KEY = 'peruselab.templates.favourites';
const MIGRATION_KEY = `${FAVOURITES_STORAGE_KEY}.migrated`;
export type FavouriteKind = 'molecule' | 'drawing';
export interface FavouriteRef {
  readonly kind: FavouriteKind;
  readonly id: string;
}
export const favouriteKey = (ref: FavouriteRef): string => `${ref.kind}:${ref.id}`;
export function parseFavouriteKey(raw: string): FavouriteRef | null {
  if (!raw) return null;
  const colon = raw.indexOf(':');
  if (colon < 0) return { kind: 'drawing', id: raw };
  const kind = raw.slice(0, colon),
    id = raw.slice(colon + 1);
  return id && (kind === 'molecule' || kind === 'drawing') ? { kind, id } : null;
}
const normalize = (value: unknown): string[] =>
  Array.isArray(value)
    ? [
        ...new Set(
          value
            .filter((item): item is string => typeof item === 'string')
            .map(parseFavouriteKey)
            .filter((item): item is FavouriteRef => item !== null)
            .map(favouriteKey)
        )
      ]
    : [];

type Status = 'local' | 'syncing' | 'saved' | 'unsaved';
interface Snapshot {
  readonly keys: readonly string[];
  readonly status: Status;
}
interface Dependencies {
  readonly storage: Pick<Storage, 'getItem' | 'setItem'>;
  readonly load: () => Promise<StoredPreferences>;
  readonly save: (revision: number, preferences: Readonly<Record<string, unknown>>) => Promise<StoredPreferences>;
}

/** Account-isolated cache and an operation queue: a stale browser never replaces the remote list. */
export function createFavouriteStore({ storage, load, save }: Dependencies) {
  const read = (key: string): unknown => {
    try {
      return JSON.parse(storage.getItem(key) ?? 'null');
    } catch {
      return null;
    }
  };
  const write = (key: string, value: unknown) => {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch {
      /* Memory-only still works. */
    }
  };
  let account: string | null = null;
  let generation = 0;
  let snapshot: Snapshot = { keys: normalize(read(FAVOURITES_STORAGE_KEY)), status: 'local' };
  let pending: Record<string, boolean> = {};
  let migration: readonly string[] = [];
  let running: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  const cacheKey = () => `${FAVOURITES_STORAGE_KEY}.account.${encodeURIComponent(account ?? '')}`;
  const notify = (keys: readonly string[], status: Status) => {
    snapshot = { keys, status };
    if (account) write(cacheKey(), { keys, pending });
    else write(FAVOURITES_STORAGE_KEY, keys);
    listeners.forEach((listener) => listener());
  };
  const apply = (keys: readonly string[], operations: Readonly<Record<string, boolean>>) => {
    const result = new Set(keys);
    for (const [key, add] of Object.entries(operations)) if (!add) result.delete(key);
    for (const [key, add] of Object.entries(operations)) if (add) result.add(key);
    return [...result];
  };
  const refresh = (): Promise<void> => {
    if (!account) return Promise.resolve();
    if (running) return running;
    const epoch = generation;
    const sync = async () => {
      notify(snapshot.keys, 'syncing');
      try {
        let base = await load();
        for (let attempt = 0; attempt < 4; attempt++) {
          if (epoch !== generation) return;
          const sent = { ...pending };
          const remote = normalize(base.preferences.templateFavourites);
          // Preserve all distinct entries during migration/conflict merges, even if the
          // merged shelf exceeds the add limit. Never discard another browser's favourites.
          // Import legacy favourites only until this browser's first successful account sync.
          const imported = apply(remote, Object.fromEntries(migration.map((key) => [key, true])));
          const next = apply(imported, sent);
          try {
            if (JSON.stringify(next) !== JSON.stringify(remote)) {
              base = await save(base.revision, { ...base.preferences, templateFavourites: next });
            }
            if (epoch !== generation) return;
            for (const [key, value] of Object.entries(sent)) if (pending[key] === value) delete pending[key];
            write(MIGRATION_KEY, true);
            migration = [];
            notify(apply(next, pending), Object.keys(pending).length ? 'syncing' : 'saved');
            if (!Object.keys(pending).length) return;
          } catch (error) {
            if (!(error instanceof PreferencesApiError) || error.status !== 409) throw error;
          }
          base = await load();
        }
        if (epoch === generation) notify(snapshot.keys, 'unsaved');
      } catch {
        if (epoch === generation) notify(snapshot.keys, 'unsaved');
      }
    };
    const task = sync().finally(() => {
      if (running === task) running = null;
    });
    running = task;
    return task;
  };
  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    setAccount(next: string | null) {
      if (next === account) return;
      generation++;
      running = null;
      account = next;
      migration = next && !read(MIGRATION_KEY) ? normalize(read(FAVOURITES_STORAGE_KEY)) : [];
      const cached = next ? (read(cacheKey()) as { keys?: unknown; pending?: unknown } | null) : null;
      pending = {};
      if (cached?.pending && typeof cached.pending === 'object') {
        for (const [key, value] of Object.entries(cached.pending)) {
          if (parseFavouriteKey(key) && typeof value === 'boolean') pending[key] = value;
        }
      }
      notify(
        next
          ? apply(normalize(cached?.keys), Object.fromEntries(migration.map((key) => [key, true])))
          : normalize(read(FAVOURITES_STORAGE_KEY)),
        next ? 'syncing' : 'local'
      );
      void refresh();
    },
    toggle(ref: FavouriteRef) {
      const key = favouriteKey(ref);
      if (!parseFavouriteKey(key)) return;
      const add = !snapshot.keys.includes(key);
      if (add && snapshot.keys.length >= MAX_FAVOURITE_TEMPLATES) return;
      if (account) pending[key] = add;
      notify(apply(snapshot.keys, { [key]: add }), account ? 'unsaved' : 'local');
      void refresh();
    },
    refresh
  };
}
